import { CustomBreadcrumbsItemType } from '../reducer/breadcrumbs';
import {
	ContentType,
	EventModel,
	FavoritePostDisplay,
	JobPostingModel,
	MaterialModel,
	PostFilters,
	PostModel,
	ProductModel,
	ServiceModel,
	VendorCompanyModel,
} from '../config/types';
import { CompanyType, companyTypes } from '../config/roles';
import { DocumentSnapshot, QuerySnapshot, Timestamp } from 'firebase/firestore';

const dashToLowercaseArray = (str: string): string[] => {
	return str.toLowerCase().split('-');
};

const camelToLowercaseArray = (str: string): string[] => {
	if (!str) {
		throw new Error(`Unable to split ${str}`);
	}
	return str
		.replace(/^\s+|\s+$/g, '')
		.replace(/URL/g, 'Url')
		.replace(/ID/g, 'Id')
		.split(/(?=[A-Z])/)
		.map((word) => word.toLowerCase());
};

const arrayToDash = (arr: string[]) => {
	return arr.join('-');
};

const capitalize = (str: string): string => {
	return str
		.split('')
		.map((letter, index) => {
			if (index === 0) {
				return letter.toUpperCase();
			}
			return letter;
		})
		.join('');
};

const arrayToCapWords = (arr: string[]) => {
	return arr
		.map((value) => capitalize(value))
		.join(' ')
		.replace(/Url/g, 'URL')
		.replace(/Id/g, 'ID');
};

const capWordsToArray = (str: string): string[] => {
	return str.toLowerCase().split(' ');
};

const arrayToCamel = (arr: string[]) => {
	for (let i = 0; i < arr.length; i++) {
		if (i !== 0) {
			arr[i] = capitalize(arr[i]);
		}
	}
	return arr.join('');
};

export const camelToDash = (str: string): string => {
	return arrayToDash(camelToLowercaseArray(str));
};

export const camelToCapWords = (str: string): string => {
	return arrayToCapWords(camelToLowercaseArray(str));
};

export const capWordsToDash = (str: string): string => {
	return arrayToDash(capWordsToArray(str));
};

export const capWordsToCamel = (str: string): string => {
	return arrayToCamel(capWordsToArray(str));
};

export const dashToCapWords = (str: string): string => {
	return arrayToCapWords(dashToLowercaseArray(str));
};

export const slugify = (str: string, companyName?: string, date?: string): string => {
	if (!str) {
		throw new Error(`Unable to slugify from ${str}`);
	}

	if (companyName) {
		str = `${str} ${companyName}`;
	}

	if (date) {
		str = `${str}-${date}`;
	}

	str = str.replace(/^\s+|\s+$/g, ''); // trim
	str = str.toLowerCase();

	// remove accents, swap ñ for n, etc
	const from = 'àáãäâèéëêìíïîòóöôùúüûñç·/_,:;';
	const to = 'aaaaaeeeeiiiioooouuuunc------';

	for (let i = 0; i < from.length; i++) {
		str = str.replace(new RegExp(from.charAt(i), 'g'), to.charAt(i));
	}

	str = str
		.replace(/[^a-z0-9 -]/g, '') // remove invalid chars
		.replace(/\s+/g, '-') // collapse whitespace and replace by -
		.replace(/-+/g, '-'); // collapse dashes

	return str;
};

export const cleanFileName = (str: string): string => {
	if (!str) {
		throw new Error(`Unable to clean the file name from ${str}`);
	}

	str = str.replace(/^\s+|\s+$/g, ''); // trim
	str = str.toLowerCase();

	// remove accents, swap ñ for n, etc
	const from = 'àáãäâèéëêìíïîòóöôùúüûñç·/_,:;';
	const to = 'aaaaaeeeeiiiioooouuuunc------';

	for (let i = 0; i < from.length; i++) {
		str = str.replace(new RegExp(from.charAt(i), 'g'), to.charAt(i));
	}

	str = str
		.replace(/[^a-z0-9 -.]/g, '') // remove invalid chars
		.replace(/\s+/g, '-') // collapse whitespace and replace by -
		.replace(/-+/g, '-'); // collapse dashes

	return str;
};

export const generateOptimizedFileName = (str: string): string => {
	if (!str) {
		throw new Error(`Unable to generate an optimized file name from ${str}`);
	}

	if (str.includes('-optimized')) {
		return cleanFileName(str);
	}

	const fileExtention = str.split('.').pop() ?? '';
	const fileNameBody = str.replace(`.${fileExtention}`, '');

	if ('svg' === fileExtention.toLowerCase()) {
		return str;
	}

	return `${cleanFileName(fileNameBody)}-optimized.${cleanFileName(fileExtention)}`;
};

export const generateImageNameFromFile = (file: File | null): string => {
	if (!file) {
		return '';
	}

	const fileName = file.name;
	const fileExtention = fileName.split('.').pop() ?? '';
	const fileNameBody = fileName.replace(`.${fileExtention}`, '');

	if ('svg' === fileExtention.toLowerCase()) {
		return fileName;
	}

	return `${cleanFileName(fileNameBody)}-optimized.${cleanFileName(fileExtention)}`;
};

export const getProviderLink = (companyName: string): string => {
	if (!companyName) {
		throw new Error(`Unable to get provider link from this name: ${companyName}`);
	}

	return `/${getPostTypeRoute('vendorCompany')}/${slugify(companyName)}`;
};

export const getPostLink = (post: any, isAdmin = false): string => {
	let route = '';
	let endpoint = '';

	if (post.postType in globalPostTypes) {
		route = globalPostTypes[post.postType].slug;
		endpoint = post.slug;
	} else {
		throw new Error(`Unknown post type: ${post.postType}.`);
	}

	if (isAdmin) {
		return `/admin/providers/${slugify(post.companyName)}/${route}/${endpoint}`;
	} else {
		return `/${route}/${endpoint}`;
	}
};

export const getFavoritePostLink = (post: FavoritePostDisplay): string => {
	let route = '';
	let endpoint = '';

	if (post.postType in globalPostTypes) {
		route = globalPostTypes[post.postType].slug;
		endpoint = slugify(post.postTitle, post.companyName);
	} else {
		throw new Error(`Unknown post type: ${post.postType}.`);
	}

	return `/${route}/${endpoint}`;
};

export const getEventLink = (post: EventModel, isAdmin = false): string => {
	const route = contentTypes.event.slug;
	const endpoint = post.slug;

	if (isAdmin) {
		return `/admin/providers/${slugify(post.companyName)}/${route}/${endpoint}`;
	} else {
		return `/${route}/${endpoint}`;
	}
};

export const getJobPostingLink = (post: JobPostingModel, isAdmin = false): string => {
	const route = contentTypes.jobPosting.slug;
	const endpoint = post.slug;

	if (isAdmin) {
		return `/admin/providers/${slugify(post.companyName)}/${route}/${endpoint}`;
	} else {
		return `/${route}/${endpoint}`;
	}
};

export const getMySpacePostLink = (post: any): string => {
	return `/my-space/${getPostTypeRoute(post.postType)}/${post.slug}`;
};

export const getMySpaceEventLink = (post: any): string => {
	return `/my-space/${contentTypes.event.slug}/${post.slug}`;
};

export const getMySpaceJobPostingLink = (post: any): string => {
	return `/my-space/${contentTypes.jobPosting.slug}/${post.slug}`;
};

export const getCompanySpacePostLink = (post: any): string => {
	return `/providers/${slugify(post.companyName)}/${getPostTypeRoute(post.postType)}/${
		post.slug
	}`;
};

export const getCompanySpaceEventLink = (post: any): string => {
	return `/providers/${slugify(post.companyName)}/${contentTypes.event.slug}/${post.slug}`;
};

export const getCompanySpaceJobPostingLink = (post: any) => {
	return `/providers/${slugify(post.companyName)}/${contentTypes.jobPosting.slug}/${post.slug}`;
};

export const getExcerpt = (text = '', limit = 210) => {
	return text.length <= limit ? text : `${text.substring(0, limit)} [...]`;
};

type GenerateCompanySpaceBreadcrumbsType = {
	currentCompany: VendorCompanyModel;
	currentTitle: string;
	currentPost?: ProductModel | ServiceModel | (EventModel & { postType: 'event' });
};

type GenerateAdminCompanySpaceBreadcrumbsType = {
	currentCompany: VendorCompanyModel;
	currentPost?:
		| PostModel
		| ProductModel
		| ServiceModel
		| (EventModel & { postType: 'event' })
		| (JobPostingModel & { postType: 'jobPosting' });
	currentTitle?: string;
};

export const generateCompanySpaceBreadcrumbs = ({
	currentCompany,
	currentTitle,
	currentPost,
}: GenerateCompanySpaceBreadcrumbsType): CustomBreadcrumbsItemType[] => {
	const items: CustomBreadcrumbsItemType[] = [
		{
			title: 'Solution Providers',
			path: '/providers',
		},
		{
			title: currentCompany.name,
			path: getProviderLink(currentCompany.name),
		},
	];

	if (currentPost) {
		items.push({
			title: postTypes[currentPost.postType].plural,
			path: `${getProviderLink(currentCompany.name)}/${getPostTypeRoute(
				currentPost.postType,
			)}`,
		});
	}

	items.push({
		title: currentTitle,
		path: '',
	});

	return items;
};

export const generateAdminCompanySpaceBreadcrumbs = ({
	currentCompany,
	currentTitle,
	currentPost,
}: GenerateAdminCompanySpaceBreadcrumbsType): CustomBreadcrumbsItemType[] => {
	const items: CustomBreadcrumbsItemType[] = [
		{
			title: 'Solution Providers',
			path: '/admin/providers',
		},
		{
			title: currentCompany.name,
			path: `/admin${getProviderLink(currentCompany.name)}`,
		},
	];

	if (currentPost) {
		items.push({
			title: postTypes[currentPost.postType].plural,
			path: `/admin${getProviderLink(currentPost.companyName)}/${getPostTypeRoute(
				currentPost.postType,
			)}`,
		});

		items.push({
			title: currentPost.title,
			path: currentTitle
				? `/admin${getProviderLink(currentPost.companyName)}/${getPostTypeRoute(
						currentPost.postType,
				  )}/${currentPost.slug}`
				: '',
		});
	}

	if (currentTitle) {
		items.push({
			title: currentTitle,
			path: '',
		});
	}

	return items;
};

type GenerateVendorContentBreadcrumbsType = {
	post: ProductModel | ServiceModel | PostModel | MaterialModel;
};

export const generateVendorContentBreadcrumbs = ({
	post,
}: GenerateVendorContentBreadcrumbsType): CustomBreadcrumbsItemType[] => {
	const items: CustomBreadcrumbsItemType[] = [
		{
			title: 'Solution Providers',
			path: '/providers',
		},
		{
			title: post.companyName,
			path: getProviderLink(post.companyName),
		},
		{
			title: postTypes[post.postType]?.plural ?? postTypes[post.postType].plural,
			path: `${getProviderLink(post.companyName)}/${getPostTypeRoute(post.postType)}`,
		},
		{
			title: post.title,
			path: '',
		},
	];

	return items;
};

export const generateVendorEventBreadcrumbs = ({ post }: any) => {
	const items: CustomBreadcrumbsItemType[] = [
		{
			title: 'Solution Providers',
			path: '/providers',
		},
		{
			title: post.companyName,
			path: getProviderLink(post.companyName),
		},
		{
			title: contentTypes.event.plural,
			path: `${getProviderLink(post.companyName)}/${contentTypes.event.slug}`,
		},
		{
			title: post.title,
			path: '',
		},
	];

	return items;
};

export const generateVendorJobPostingBreadcrumbs = ({ post }: any) => {
	const items: CustomBreadcrumbsItemType[] = [
		{
			title: 'Solution Providers',
			path: '/providers',
		},
		{
			title: post.companyName,
			path: getProviderLink(post.companyName),
		},
		{
			title: contentTypes.jobPosting.plural,
			path: `${getProviderLink(post.companyName)}/${contentTypes.jobPosting.slug}`,
		},
		{
			title: post.title,
			path: '',
		},
	];

	return items;
};

type GeneratePostTypeSingleBreadcrumbsType = {
	post: MaterialModel;
};

export const generatePostTypeSingleBreadcrumbs = ({
	post,
}: GeneratePostTypeSingleBreadcrumbsType): CustomBreadcrumbsItemType[] => {
	const items: CustomBreadcrumbsItemType[] = [
		{
			title: globalPostTypes[post.postType].plural,
			path: `/${globalPostTypes[post.postType].slug}`,
		},
		{
			title: post.title,
			path: '',
		},
	];

	return items;
};

type GeneratePostTypeArchiveBreadcrumbsType = {
	postType: string;
};

export const generatePostTypeArchiveBreadcrumbs = ({
	postType,
}: GeneratePostTypeArchiveBreadcrumbsType): CustomBreadcrumbsItemType[] => {
	const items: CustomBreadcrumbsItemType[] = [
		{
			title: globalPostTypes[postType].plural,
			path: '',
		},
	];

	return items;
};

export const generateMySpaceContentBreadcrumbs = ({
	post,
}: GenerateVendorContentBreadcrumbsType): CustomBreadcrumbsItemType[] => {
	const items: CustomBreadcrumbsItemType[] = [
		{
			title: 'My space',
			path: '/my-space',
		},
		{
			title: postTypes[post.postType].plural,
			path: `/my-space/${getPostTypeRoute(post.postType)}`,
		},
		{
			title: post.title,
			path: '',
		},
	];

	return items;
};

export const generateMySpaceEventBreadcrumbs = ({ post }: any) => {
	const items: CustomBreadcrumbsItemType[] = [
		{
			title: 'My space',
			path: '/my-space',
		},
		{
			title: contentTypes.event.plural,
			path: `/my-space/${contentTypes.event.slug}`,
		},
		{
			title: post.title,
			path: '',
		},
	];

	return items;
};

export const generateMySpaceJobPostingBreadcrumbs = ({ post }: any) => {
	const items: CustomBreadcrumbsItemType[] = [
		{
			title: 'My space',
			path: '/my-space',
		},
		{
			title: contentTypes.jobPosting.plural,
			path: `/my-space/${contentTypes.jobPosting.slug}`,
		},
		{
			title: post.title,
			path: '',
		},
	];

	return items;
};

type GenerateBreadcrumbsType = {
	post: ProductModel | ServiceModel | PostModel;
	currentTitle: string;
	isMySpace?: boolean;
};

export const generatePageBreadcrumbs = ({
	post,
	currentTitle,
	isMySpace = false,
}: GenerateBreadcrumbsType): CustomBreadcrumbsItemType[] => {
	let items: CustomBreadcrumbsItemType[] = [];
	let prePath = '';

	if (isMySpace) {
		items.push({
			title: 'My space',
			path: '/my-space',
		});
		prePath = '/my-space';
	}
	items = [
		...items,
		{
			title: getGeneralPostType(post.postType).plural,
			path: `${prePath}/${getPostTypeRoute(post.postType)}`,
		},
		{
			title: post.title,
			path: `${prePath}${getPostLink(post)}`,
		},
		{
			title: currentTitle,
			path: '',
		},
	];

	return items;
};

export const generateEventBreadcrumbs = ({ post, currentTitle, isMySpace = false }: any) => {
	let items: CustomBreadcrumbsItemType[] = [];
	let prePath = '';

	if (isMySpace) {
		items.push({
			title: 'My space',
			path: '/my-space',
		});
		prePath = '/my-space';
	}
	items = [
		...items,
		{
			title: contentTypes.event.plural,
			path: `${prePath}/${contentTypes.event.slug}`,
		},
		{
			title: post.title,
			path: `${prePath}${getEventLink(post)}`,
		},
		{
			title: currentTitle,
			path: '',
		},
	];

	return items;
};

export const generateJobPostingBreadcrumbs = ({ post, currentTitle, isMySpace = false }: any) => {
	let items: CustomBreadcrumbsItemType[] = [];
	let prePath = '';

	if (isMySpace) {
		items.push({
			title: 'My space',
			path: '/my-space',
		});
		prePath = '/my-space';
	}
	items = [
		...items,
		{
			title: contentTypes.jobPosting.plural,
			path: `${prePath}/${contentTypes.jobPosting.slug}`,
		},
		{
			title: post.title,
			path: `${prePath}${getEventLink(post)}`,
		},
		{
			title: currentTitle,
			path: '',
		},
	];

	return items;
};

export const getPostCategoryUrl = (slug: string) => {
	return `/posts/category/${slug}`;
};

export const getPostTypeRoute = (postType: string) => {
	let postTypeRoute = '';

	switch (postType) {
		case 'post':
			postTypeRoute = 'posts';
			break;

		case 'product':
			postTypeRoute = 'products';
			break;

		case 'service':
			postTypeRoute = 'services';
			break;

		case 'event':
			postTypeRoute = 'events';
			break;

		case 'jobPosting':
			postTypeRoute = 'job-postings';
			break;

		case 'vendorCompany':
		case 'vendor':
			postTypeRoute = 'providers';
			break;

		case 'caseStudy':
			postTypeRoute = 'case-studies';
			break;

		case 'whitePaper':
			postTypeRoute = 'white-papers';
			break;

		case 'videoLink':
			postTypeRoute = 'video-links';
			break;

		case 'userManual':
			postTypeRoute = 'user-manuals';
			break;

		case 'adminGuide':
			postTypeRoute = 'admin-guides';
			break;

		case 'releaseNote':
			postTypeRoute = 'release-notes';
			break;

		case 'marketingMaterial':
			postTypeRoute = 'marketing-materials';
			break;

		case 'question':
			postTypeRoute = 'questions';
			break;

		default:
			throw new Error(`Unknown postType: ${postType}`);
	}

	return postTypeRoute;
};

export const getPostTypeByRoute = (route: string) => {
	let postType = '';

	switch (route) {
		case 'posts':
			postType = 'post';
			break;

		case 'products':
			postType = 'product';
			break;

		case 'services':
			postType = 'service';
			break;

		case 'providers':
			postType = 'vendor';
			break;

		case 'case-studies':
			postType = 'caseStudy';
			break;

		case 'white-papers':
			postType = 'whitePaper';
			break;

		case 'video-links':
			postType = 'videoLink';
			break;

		case 'user-manuals':
			postType = 'userManual';
			break;

		case 'admin-guides':
			postType = 'adminGuide';
			break;

		case 'release-notes':
			postType = 'releaseNote';
			break;

		case 'marketing-materials':
			postType = 'marketingMaterial';
			break;

		case 'questions':
			postType = 'question';
			break;

		case 'events':
			postType = 'event';
			break;

		case 'job-postings':
			postType = 'jobPosting';
			break;

		default:
			throw new Error(`Unknown post type route: ${route}`);
	}

	return postType;
};

export const getPostTypeTaxonomyArchiveUrl = (postType: string, taxonomy: string) => {
	return `/${getPostTypeRoute(postType)}/${taxonomy}`;
};

export const getPostTypeTaxonomyUrl = (postType: string, taxonomy: string, slug: string) => {
	return `${getPostTypeTaxonomyArchiveUrl(postType, taxonomy)}/${slug}`;
};

export const formatDateFromMillis = (timestamp: number, unix = false) => {
	const newTimestamp = unix ? +timestamp * 1000 : +timestamp;

	const date = new Date(newTimestamp);

	const month = date.getMonth() + 1;
	const day = date.getDate();
	const year = date.getFullYear();

	return `${month}/${day}/${year}`;
};

export const formatDateFromString = (timestamp: string) => {
	const date = new Date(timestamp);

	const month = date.getMonth() + 1;
	const day = date.getDate();
	const year = date.getFullYear();

	return `${month}/${day}/${year}`;
};

export const formatDateFromTimestamp = (timestamp: Timestamp) =>
	formatDateFromMillis(timestamp.toMillis());

export const formatTimeFromMillis = (timestamp: number, unix = false) => {
	const newTimestamp = unix ? +timestamp * 1000 : +timestamp;

	const date = new Date(newTimestamp);
	let hours = date.getHours();
	const meridian = hours > 11 ? 'PM' : 'AM';
	hours = hours > 11 ? hours - 12 : hours;

	let minutes: number | string = date.getMinutes();
	minutes = minutes > 9 ? minutes : `0${minutes}`;

	return `${hours}:${minutes} ${meridian}`;
};

export const formatTimeFromString = (timestamp: string) => {
	const date = new Date(timestamp);
	let hours = date.getHours();
	const meridian = hours > 11 ? 'PM' : 'AM';
	hours = hours > 11 ? hours - 12 : hours;

	let minutes: number | string = date.getMinutes();
	minutes = minutes > 9 ? minutes : `0${minutes}`;

	return `${hours}:${minutes} ${meridian}`;
};

export const formatTimeFromTimestamp = (timestamp: Timestamp) =>
	formatTimeFromMillis(timestamp.toMillis());

export const getInitialLetters = (name: string) => {
	const words: string[] = name.split(' ');

	let letters = '';

	words.forEach((word: string) => {
		if (word) {
			letters += word[0].toUpperCase();
		}
	});

	return letters;
};

type MaterialType = {
	singular: string;
	plural: string;
	slug: string;
	children?: {
		[key: string]: TaxonomyType;
	};
};

type MaterialTypes = {
	[name: string]: MaterialType;
};

type StatusType = {
	[slug: string]: {
		label: string;
	};
};

type QuestionStatusType = {
	[slug: string]: {
		label: string;
		color: string;
	};
};

export const postStatuses: StatusType = {
	publish: {
		label: 'Publish',
	},
	archived: {
		label: 'Archived',
	},
};

export const vendorStatuses: StatusType = {
	active: {
		label: 'Active',
	},
	onHold: {
		label: 'On hold',
	},
	disabled: {
		label: 'Disabled',
	},
};

export const questionStatuses: QuestionStatusType = {
	open: {
		label: 'Open',
		color: 'blue',
	},
	resolved: {
		label: 'Resolved',
		color: 'green',
	},
};

type TaxonomyType = {
	singular: string;
	plural: string;
	slug: string;
};

type TaxonomiesType = {
	[key in ContentType]: TaxonomyType;
};

export const taxonomies: TaxonomiesType = {
	category: {
		singular: 'Category',
		plural: 'Categories',
		slug: 'categories',
	},
	tag: {
		singular: 'Tag',
		plural: 'Tags',
		slug: 'tags',
	},
	'pricing-model': {
		singular: 'Pricing model',
		plural: 'Pricing models',
		slug: 'pricing-models',
	},
};

type PostTypeType = {
	singular: string;
	plural: string;
	slug: string;
	children?: {
		[key: string]: PostTypeType;
	};
};

type PostTypeTypes = {
	[key: string]: PostTypeType;
};

export const postTypeTypes: PostTypeTypes = {
	product: {
		singular: 'Product',
		plural: 'Products',
		slug: 'products',
	},
	service: {
		singular: 'Service',
		plural: 'Services',
		slug: 'services',
	},
	post: {
		singular: 'Post',
		plural: 'Posts',
		slug: 'posts',
	},
};

export const productMaterialTypes: PostTypeTypes = {
	caseStudy: {
		singular: 'Case Study',
		plural: 'Case Studies',
		slug: 'case-studies',
	},
	whitePaper: {
		singular: 'White Paper',
		plural: 'White Papers',
		slug: 'white-papers',
	},
	videoLink: {
		singular: 'Video Link',
		plural: 'Video Links',
		slug: 'video-links',
		children: {
			howTo: {
				singular: 'How-To',
				plural: 'How-Tos',
				slug: 'how-tos',
			},
			training: {
				singular: 'Training',
				plural: 'Trainings',
				slug: 'trainings',
			},
			marketing: {
				singular: 'Marketing',
				plural: 'Marketing',
				slug: 'marketing',
			},
			interview: {
				singular: 'Interview',
				plural: 'Interviews',
				slug: 'interview',
			},
			demonstration: {
				singular: 'Demonstration',
				plural: 'Demonstrations',
				slug: 'demonstration',
			},
			webinar: {
				singular: 'Webinar',
				plural: 'Webinars',
				slug: 'webinar',
			},
			integration: {
				singular: 'Integration',
				plural: 'Integrations',
				slug: 'integration',
			},
		},
	},
	userManual: {
		singular: 'User Manual',
		plural: 'User Manuals',
		slug: 'user-manuals',
	},
	adminGuide: {
		singular: 'Admin Guide',
		plural: 'Admin Guides',
		slug: 'admin-guides',
	},
	releaseNote: {
		singular: 'Release Note',
		plural: 'Release Notes',
		slug: 'release-notes',
	},
	marketingMaterial: {
		singular: 'Marketing Material',
		plural: 'Marketing Content',
		slug: 'marketing-materials',
	},
};

export const globalPostTypes: PostTypeTypes = {
	...postTypeTypes,
	...productMaterialTypes,
};

type SelectOption = {
	label: string;
	value: string;
};

export const generateProductMaterialTypesOptions = (): SelectOption[] => {
	const options: SelectOption[] = [];

	Object.keys(productMaterialTypes).forEach((slug: string) => {
		options.push({
			label: productMaterialTypes[slug].singular,
			value: slug,
		});
	});

	return options;
};

export const postTypes: MaterialTypes = {
	product: {
		singular: 'Product',
		plural: 'Products',
		slug: 'products',
	},
	service: {
		singular: 'Service',
		plural: 'Services',
		slug: 'services',
	},
	post: {
		singular: 'Post',
		plural: 'Posts',
		slug: 'posts',
	},
	event: {
		singular: 'Event',
		plural: 'Events',
		slug: 'events',
	},
	jobPosting: {
		singular: 'Job Posting',
		plural: 'Job Postings',
		slug: 'job-postings',
	},
};

export const contentTypes: MaterialTypes = {
	product: {
		singular: 'Product',
		plural: 'Products',
		slug: 'products',
	},
	service: {
		singular: 'Service',
		plural: 'Services',
		slug: 'services',
	},
	post: {
		singular: 'Post',
		plural: 'Posts',
		slug: 'posts',
	},
	content: {
		singular: 'Content',
		plural: 'Content',
		slug: 'content',
	},
	event: {
		singular: 'Event',
		plural: 'Events',
		slug: 'events',
	},
	jobPosting: {
		singular: 'Job Posting',
		plural: 'Job Postings',
		slug: 'job-postings',
	},
};

export const getPostType = (slug: string): MaterialType => postTypes[slug];
export const getMaterialType = (slug: string): MaterialType => productMaterialTypes[slug];

export const getGeneralPostType = (slug: string): MaterialType =>
	productMaterialTypes[slug] ?? postTypes[slug];

export const getGeneralPostSubtype = (parentSlug: string, slug: string): MaterialType =>
	productMaterialTypes[parentSlug].children?.[slug] ??
	(postTypes[parentSlug].children?.[slug] as MaterialType);

export const formatToISOPhone = (phone: string) => {
	return phone.replace(/-|\(|\)|\.|\s/g, '');
};

interface CountriesWithPhoneCode {
	[countryShortCode: string]: { countryName: string };
}

export const countriesWithPhoneCode: CountriesWithPhoneCode = {
	ad: { countryName: 'Andorra' },
	ae: { countryName: 'United Arab Emirates' },
	af: { countryName: 'Afghanistan' },
	ag: { countryName: 'Antigua and Barbuda' },
	ai: { countryName: 'Anguilla' },
	al: { countryName: 'Albania' },
	am: { countryName: 'Armenia' },
	ao: { countryName: 'Angola' },
	ar: { countryName: 'Argentina' },
	as: { countryName: 'American Samoa' },
	at: { countryName: 'Austria' },
	au: { countryName: 'Australia' },
	aw: { countryName: 'Aruba' },
	az: { countryName: 'Azerbaijan' },
	ba: { countryName: 'Bosnia and Herzegovina' },
	bb: { countryName: 'Barbados' },
	bd: { countryName: 'Bangladesh' },
	be: { countryName: 'Belgium' },
	bf: { countryName: 'Burkina Faso' },
	bg: { countryName: 'Bulgaria' },
	bh: { countryName: 'Bahrain' },
	bi: { countryName: 'Burundi' },
	bj: { countryName: 'Benin' },
	bl: { countryName: 'Saint Barthélemy' },
	bm: { countryName: 'Bermuda' },
	bn: { countryName: 'Brunei' },
	bo: { countryName: 'Bolivia' },
	bq: { countryName: 'Caribbean Netherlands' },
	br: { countryName: 'Brazil' },
	bs: { countryName: 'Bahamas' },
	bt: { countryName: 'Bhutan' },
	bw: { countryName: 'Botswana' },
	by: { countryName: 'Belarus' },
	bz: { countryName: 'Belize' },
	ca: { countryName: 'Canada' },
	cd: { countryName: 'Congo' },
	cf: { countryName: 'Central African Republic' },
	cg: { countryName: 'Congo' },
	ch: { countryName: 'Switzerland' },
	ci: { countryName: 'Côte d’Ivoire' },
	ck: { countryName: 'Cook Islands' },
	cl: { countryName: 'Chile' },
	cm: { countryName: 'Cameroon' },
	cn: { countryName: 'China' },
	co: { countryName: 'Colombia' },
	cr: { countryName: 'Costa Rica' },
	cu: { countryName: 'Cuba' },
	cv: { countryName: 'Cape Verde' },
	cw: { countryName: 'Curaçao' },
	cy: { countryName: 'Cyprus' },
	cz: { countryName: 'Czech Republic' },
	de: { countryName: 'Germany' },
	dj: { countryName: 'Djibouti' },
	dk: { countryName: 'Denmark' },
	dm: { countryName: 'Dominica' },
	do: { countryName: 'Dominican Republic' },
	dz: { countryName: 'Algeria' },
	ec: { countryName: 'Ecuador' },
	ee: { countryName: 'Estonia' },
	eg: { countryName: 'Egypt' },
	er: { countryName: 'Eritrea' },
	es: { countryName: 'Spain' },
	et: { countryName: 'Ethiopia' },
	fi: { countryName: 'Finland' },
	fj: { countryName: 'Fiji' },
	fk: { countryName: 'Falkland Islands' },
	fm: { countryName: 'Micronesia' },
	fo: { countryName: 'Faroe Islands' },
	fr: { countryName: 'France' },
	ga: { countryName: 'Gabon' },
	gb: { countryName: 'United Kingdom' },
	gd: { countryName: 'Grenada' },
	ge: { countryName: 'Georgia' },
	gf: { countryName: 'French Guiana' },
	gh: { countryName: 'Ghana' },
	gi: { countryName: 'Gibraltar' },
	gl: { countryName: 'Greenland' },
	gm: { countryName: 'Gambia' },
	gn: { countryName: 'Guinea' },
	gp: { countryName: 'Guadeloupe' },
	gq: { countryName: 'Equatorial Guinea' },
	gr: { countryName: 'Greece' },
	gt: { countryName: 'Guatemala' },
	gu: { countryName: 'Guam' },
	gw: { countryName: 'Guinea-Bissau' },
	gy: { countryName: 'Guyana' },
	hk: { countryName: 'Hong Kong' },
	hn: { countryName: 'Honduras' },
	hr: { countryName: 'Croatia' },
	ht: { countryName: 'Haiti' },
	hu: { countryName: 'Hungary' },
	id: { countryName: 'Indonesia' },
	ie: { countryName: 'Ireland' },
	il: { countryName: 'Israel' },
	in: { countryName: 'India' },
	io: { countryName: 'British Indian Ocean Territory' },
	iq: { countryName: 'Iraq' },
	ir: { countryName: 'Iran' },
	is: { countryName: 'Iceland' },
	it: { countryName: 'Italy' },
	jm: { countryName: 'Jamaica' },
	jo: { countryName: 'Jordan' },
	jp: { countryName: 'Japan' },
	ke: { countryName: 'Kenya' },
	kg: { countryName: 'Kyrgyzstan' },
	kh: { countryName: 'Cambodia' },
	ki: { countryName: 'Kiribati' },
	km: { countryName: 'Comoros' },
	kn: { countryName: 'Saint Kitts and Nevis' },
	kp: { countryName: 'North Korea' },
	kr: { countryName: 'South Korea' },
	kw: { countryName: 'Kuwait' },
	ky: { countryName: 'Cayman Islands' },
	kz: { countryName: 'Kazakhstan' },
	la: { countryName: 'Laos' },
	lb: { countryName: 'Lebanon' },
	lc: { countryName: 'Saint Lucia' },
	li: { countryName: 'Liechtenstein' },
	lk: { countryName: 'Sri Lanka' },
	lr: { countryName: 'Liberia' },
	ls: { countryName: 'Lesotho' },
	lt: { countryName: 'Lithuania' },
	lu: { countryName: 'Luxembourg' },
	lv: { countryName: 'Latvia' },
	ly: { countryName: 'Libya' },
	ma: { countryName: 'Morocco' },
	mc: { countryName: 'Monaco' },
	md: { countryName: 'Moldova' },
	me: { countryName: 'Montenegro' },
	mf: { countryName: 'Saint Martin' },
	mg: { countryName: 'Madagascar' },
	mh: { countryName: 'Marshall Islands' },
	mk: { countryName: 'Macedonia' },
	ml: { countryName: 'Mali' },
	mm: { countryName: 'Myanmar' },
	mn: { countryName: 'Mongolia' },
	mo: { countryName: 'Macau' },
	mp: { countryName: 'Northern Mariana Islands' },
	mq: { countryName: 'Martinique' },
	mr: { countryName: 'Mauritania' },
	ms: { countryName: 'Montserrat' },
	mt: { countryName: 'Malta' },
	mu: { countryName: 'Mauritius' },
	mv: { countryName: 'Maldives' },
	mw: { countryName: 'Malawi' },
	mx: { countryName: 'Mexico' },
	my: { countryName: 'Malaysia' },
	mz: { countryName: 'Mozambique' },
	na: { countryName: 'Namibia' },
	nc: { countryName: 'New Caledonia' },
	ne: { countryName: 'Niger' },
	nf: { countryName: 'Norfolk Island' },
	ng: { countryName: 'Nigeria' },
	ni: { countryName: 'Nicaragua' },
	nl: { countryName: 'Netherlands' },
	no: { countryName: 'Norway' },
	np: { countryName: 'Nepal' },
	nr: { countryName: 'Nauru' },
	nu: { countryName: 'Niue' },
	nz: { countryName: 'New Zealand' },
	om: { countryName: 'Oman' },
	pa: { countryName: 'Panama' },
	pe: { countryName: 'Peru' },
	pf: { countryName: 'French Polynesia' },
	pg: { countryName: 'Papua New Guinea' },
	ph: { countryName: 'Philippines' },
	pk: { countryName: 'Pakistan' },
	pl: { countryName: 'Poland' },
	pm: { countryName: 'Saint Pierre and Miquelon' },
	pr: { countryName: 'Puerto Rico' },
	ps: { countryName: 'Palestine' },
	pt: { countryName: 'Portugal' },
	pw: { countryName: 'Palau' },
	py: { countryName: 'Paraguay' },
	qa: { countryName: 'Qatar' },
	re: { countryName: 'Réunion' },
	ro: { countryName: 'Romania' },
	rs: { countryName: 'Serbia' },
	ru: { countryName: 'Russia' },
	rw: { countryName: 'Rwanda' },
	sa: { countryName: 'Saudi Arabia' },
	sb: { countryName: 'Solomon Islands' },
	sc: { countryName: 'Seychelles' },
	sd: { countryName: 'Sudan' },
	se: { countryName: 'Sweden' },
	sg: { countryName: 'Singapore' },
	sh: { countryName: 'Saint Helena' },
	si: { countryName: 'Slovenia' },
	sk: { countryName: 'Slovakia' },
	sl: { countryName: 'Sierra Leone' },
	sm: { countryName: 'San Marino' },
	sn: { countryName: 'Senegal' },
	so: { countryName: 'Somalia' },
	sr: { countryName: 'Suriname' },
	ss: { countryName: 'South Sudan' },
	st: { countryName: 'São Tomé and Príncipe' },
	sv: { countryName: 'El Salvador' },
	sx: { countryName: 'Sint Maarten' },
	sy: { countryName: 'Syria' },
	sz: { countryName: 'Swaziland' },
	tc: { countryName: 'Turks and Caicos Islands' },
	td: { countryName: 'Chad' },
	tg: { countryName: 'Togo' },
	th: { countryName: 'Thailand' },
	tj: { countryName: 'Tajikistan' },
	tk: { countryName: 'Tokelau' },
	tl: { countryName: 'Timor-Leste' },
	tm: { countryName: 'Turkmenistan' },
	tn: { countryName: 'Tunisia' },
	to: { countryName: 'Tonga' },
	tr: { countryName: 'Turkey' },
	tt: { countryName: 'Trinidad and Tobago' },
	tv: { countryName: 'Tuvalu' },
	tw: { countryName: 'Taiwan' },
	tz: { countryName: 'Tanzania' },
	ua: { countryName: 'Ukraine' },
	ug: { countryName: 'Uganda' },
	us: { countryName: 'United States' },
	uy: { countryName: 'Uruguay' },
	uz: { countryName: 'Uzbekistan' },
	va: { countryName: 'Vatican City' },
	vc: { countryName: 'Saint Vincent and the Grenadines' },
	ve: { countryName: 'Venezuela' },
	vg: { countryName: 'British Virgin Islands' },
	vi: { countryName: 'U.S. Virgin Islands' },
	vn: { countryName: 'Vietnam' },
	vu: { countryName: 'Vanuatu' },
	wf: { countryName: 'Wallis and Futuna' },
	ws: { countryName: 'Samoa' },
	ye: { countryName: 'Yemen' },
	za: { countryName: 'South Africa' },
	zm: { countryName: 'Zambia' },
	zw: { countryName: 'Zimbabwe' },
};

interface CountriesWithPhoneFormat {
	[countryShortCode: string]: {
		countryName: string;
		phoneFormat: string;
	};
}

export const countriesWithPhoneFormat: CountriesWithPhoneFormat = {
	ar: { countryName: 'Argentina', phoneFormat: '+NN (NN) NNNNNNNN' },
	au: { countryName: 'Australia', phoneFormat: '+NN NNN NNN NNN' },
	be: { countryName: 'Belgium', phoneFormat: '+NN NNN NN NN NN' },
	br: { countryName: 'Brazil', phoneFormat: '+NN (NN) NNNNNNNNN' },
	by: { countryName: 'Belarus', phoneFormat: '+NNN (NN) NNN NN NN' },
	ca: { countryName: 'Canada', phoneFormat: '+N (NNN) NNN-NNNN' },
	ch: { countryName: 'Switzerland', phoneFormat: '+NN NN NNN NN NN' },
	cn: { countryName: 'China', phoneFormat: '+NN NN-NNNNNNNNN' },
	cr: { countryName: 'Costa Rica', phoneFormat: '+NNN NNNN-NNNN' },
	cy: { countryName: 'Cyprus', phoneFormat: '+NNN NN NNNNNN' },
	de: { countryName: 'Germany', phoneFormat: '+NN NNNN NNNNNNNN' },
	dk: { countryName: 'Denmark', phoneFormat: '+NN NN NN NN NN' },
	ee: { countryName: 'Estonia', phoneFormat: '+NNN NNNN NNNNNN' },
	es: { countryName: 'Spain', phoneFormat: '+NN NNN NNN NNN' },
	fi: { countryName: 'Finland', phoneFormat: '+NNN NN NNN NN NN' },
	fr: { countryName: 'France', phoneFormat: '+NN N NN NN NN NN' },
	gb: { countryName: 'United Kingdom', phoneFormat: '+NN NNNN NNNNNN' },
	gt: { countryName: 'Guatemala', phoneFormat: '+NNN NNNN-NNNN' },
	hk: { countryName: 'Hong Kong', phoneFormat: '+NNN NNNN NNNN' },
	ht: { countryName: 'Haiti', phoneFormat: '+NNN NNNN-NNNN' },
	ie: { countryName: 'Ireland', phoneFormat: '+NNN NN NNNNNNN' },
	il: { countryName: 'Israel', phoneFormat: '+NNN NNN NNN NNNN' },
	in: { countryName: 'India', phoneFormat: '+NN NNNNN-NNNNN' },
	is: { countryName: 'Iceland', phoneFormat: '+NNN NNN NNNN' },
	it: { countryName: 'Italy', phoneFormat: '+NN NNN NNNNNNN' },
	jp: { countryName: 'Japan', phoneFormat: '+NN NN NNNN NNNN' },
	kr: { countryName: 'South Korea', phoneFormat: '+NN NNN NNNN NNNN' },
	kz: { countryName: 'Kazakhstan', phoneFormat: '+N NNN NNN-NN-NN' },
	md: { countryName: 'Moldova', phoneFormat: '+NNN (NN) NN-NN-NN' },
	my: { countryName: 'Malaysia', phoneFormat: '+NN NN-NNNN-NNNN' },
	nl: { countryName: 'Netherlands', phoneFormat: '+NN NN NNNNNNNN' },
	no: { countryName: 'Norway', phoneFormat: '+NN NNN NN NNN' },
	nz: { countryName: 'New Zealand', phoneFormat: '+NN NNN-NNN-NNNN' },
	ph: { countryName: 'Philippines', phoneFormat: '+NN NNNN NNNNNNN' },
	pk: { countryName: 'Pakistan', phoneFormat: '+NN NNN-NNNNNNN' },
	pl: { countryName: 'Poland', phoneFormat: '+NN NNN-NNN-NNN' },
	ru: { countryName: 'Russia', phoneFormat: '+N (NNN) NNN-NN-NN' },
	se: { countryName: 'Sweden', phoneFormat: '+NN (NN) NNN-NN-NN' },
	sg: { countryName: 'Singapore', phoneFormat: '+NN NNNN-NNNN' },
	sv: { countryName: 'El Salvador', phoneFormat: '+NNN NNNN-NNNN' },
	tr: { countryName: 'Turkey', phoneFormat: '+NN NNN NNN NN NN' },
	ua: { countryName: 'Ukraine', phoneFormat: '+NNN (NN) NNN NN NN' },
	us: { countryName: 'United States', phoneFormat: '+N (NNN) NNN-NNNN' },
	va: { countryName: 'Vatican City', phoneFormat: '+NN NN NNNN NNNN' },
};

type UserRole = {
	singular: string;
	plural: string;
};

type UserRoles = {
	[key: string]: UserRole;
};

export const userRoles: UserRoles = {
	admin: {
		singular: 'Administrator',
		plural: 'Administrators',
	},
	vendor: {
		singular: 'Solution provider',
		plural: 'Solution providers',
	},
	member: {
		singular: 'Member',
		plural: 'members',
	},
};

export const postFilters: PostFilters = {
	vendor: {
		default: 'lastName',
		options: {
			lastName: {
				orderBy: 'lastName',
				order: 'asc',
				label: 'By last name',
			},
			firstName: {
				orderBy: 'firstName',
				order: 'asc',
				label: 'By first name',
			},
			companyId: {
				orderBy: 'companyId',
				order: 'asc',
				label: 'By company',
			},
		},
	},
	vendorCompany: {
		default: 'createdAt',
		options: {
			createdAt: {
				orderBy: 'createdAt',
				order: 'desc',
				label: 'Recently added',
			},
			name: {
				orderBy: 'name',
				order: 'asc',
				label: 'By name',
			},
		},
	},
	post: {
		default: 'createdAt',
		options: {
			createdAt: {
				orderBy: 'createdAt',
				order: 'desc',
				label: 'Recently added',
			},
			views: {
				orderBy: 'views',
				order: 'desc',
				label: 'Most popular',
			},
		},
	},
	caseStudy: {
		default: 'createdAt',
		options: {
			createdAt: {
				orderBy: 'createdAt',
				order: 'desc',
				label: 'Recently added',
			},
			views: {
				orderBy: 'views',
				order: 'desc',
				label: 'Most popular',
			},
		},
	},
	whitePaper: {
		default: 'createdAt',
		options: {
			createdAt: {
				orderBy: 'createdAt',
				order: 'desc',
				label: 'Recently added',
			},
			views: {
				orderBy: 'views',
				order: 'desc',
				label: 'Most popular',
			},
		},
	},
	videoLink: {
		default: 'createdAt',
		options: {
			createdAt: {
				orderBy: 'createdAt',
				order: 'desc',
				label: 'Recently added',
			},
			views: {
				orderBy: 'views',
				order: 'desc',
				label: 'Most popular',
			},
		},
	},
	userManual: {
		default: 'createdAt',
		options: {
			createdAt: {
				orderBy: 'createdAt',
				order: 'desc',
				label: 'Recently added',
			},
			views: {
				orderBy: 'views',
				order: 'desc',
				label: 'Most popular',
			},
		},
	},
	adminGuide: {
		default: 'createdAt',
		options: {
			createdAt: {
				orderBy: 'createdAt',
				order: 'desc',
				label: 'Recently added',
			},
			views: {
				orderBy: 'views',
				order: 'desc',
				label: 'Most popular',
			},
		},
	},
	releaseNote: {
		default: 'createdAt',
		options: {
			createdAt: {
				orderBy: 'createdAt',
				order: 'desc',
				label: 'Recently added',
			},
			views: {
				orderBy: 'views',
				order: 'desc',
				label: 'Most popular',
			},
		},
	},
	marketingMaterial: {
		default: 'createdAt',
		options: {
			createdAt: {
				orderBy: 'createdAt',
				order: 'desc',
				label: 'Recently added',
			},
			views: {
				orderBy: 'views',
				order: 'desc',
				label: 'Most popular',
			},
		},
	},
	product: {
		default: 'createdAt',
		options: {
			createdAt: {
				orderBy: 'createdAt',
				order: 'desc',
				label: 'Recently added',
			},
			likes: {
				orderBy: 'likes',
				order: 'desc',
				label: 'Most popular',
			},
		},
	},
	service: {
		default: 'createdAt',
		options: {
			createdAt: {
				orderBy: 'createdAt',
				order: 'desc',
				label: 'Recently added',
			},
			likes: {
				orderBy: 'likes',
				order: 'desc',
				label: 'Most popular',
			},
		},
	},
	question: {
		default: 'createdAt',
		options: {
			createdAt: {
				orderBy: 'createdAt',
				order: 'desc',
				label: 'Recently added',
			},
			votes: {
				orderBy: 'votes',
				order: 'desc',
				label: 'Most voted',
			},
			views: {
				orderBy: 'views',
				order: 'desc',
				label: 'Most viewed',
			},
		},
	},
	jobPosting: {
		default: 'title',
		options: {
			title: {
				orderBy: 'title',
				order: 'asc',
				label: 'By title',
			},
			views: {
				orderBy: 'views',
				order: 'desc',
				label: 'Most viewed',
			},
		},
	},
	event: {
		default: 'datetime',
		options: {
			datetime: {
				orderBy: 'datetime',
				order: 'asc',
				label: 'Upcoming',
			},
			views: {
				orderBy: 'views',
				order: 'desc',
				label: 'Most viewed',
			},
		},
	},
};

export const getCompanyTypesList = (slugs: CompanyType[]) => {
	return slugs.reduce((acc, cur, index) => {
		acc += companyTypes[cur].singular;

		if (index !== slugs.length - 1) {
			acc += ', ';
		}

		return acc;
	}, '');
};

export const getContentTagsList = (termIds: string[], terms: any) => {
	if (!terms) {
		return '-';
	}
	return termIds.reduce((acc, cur, index) => {
		acc += terms.tag[cur].name;

		if (index !== termIds.length - 1) {
			acc += ', ';
		}

		return acc;
	}, '');
};

export const getCompanyAreasList = (termIds: string[], terms: any) => {
	if (!terms) {
		return '-';
	}

	return termIds.reduce((acc, cur, index) => {
		acc += terms.category[cur].name;

		if (index !== termIds.length - 1) {
			acc += ', ';
		}

		return acc;
	}, '');
};

export const getTermBySlugAndTaxonomy = (
	slug: string,
	taxonomy: 'category' | 'tag',
	terms: any,
) => {
	if (!slug || !taxonomy || !Object.keys(terms).length) {
		return null;
	}

	if (undefined === terms[taxonomy]) {
		console.error(`Unknown taxonomy: ${taxonomy}`);
		return null;
	}

	const match = Object.keys(terms[taxonomy]).filter(
		(termId: string) => terms[taxonomy][termId].slug === slug,
	);

	if (!match.length) {
		console.error(`Could not find a term with slug: ${slug}`);
		return null;
	}

	return terms[taxonomy][match[0]];
};

export const convertSnapshotToArray = (snapshot: QuerySnapshot | undefined) => {
	const result = snapshot?.docs?.reduce((acc: any, doc) => {
		const data = doc.data();

		acc.push({
			id: doc.id,
			...data,
		});

		return acc;
	}, []);

	return result || [];
};

export const convertDocToPost = (doc: DocumentSnapshot | undefined) => {
	if (!doc) {
		return null;
	}

	return {
		id: doc.id,
		...doc.data(),
	};
};

export const sanityString = (value: string) => value.replace(/^\s+/, '').replace(/\s+$/, '');

export const escList = (list: string): string[] =>
	list
		.split(',')
		.map((value: string) => sanityString(value))
		.filter((value: string) => value !== '');

export const escUrl = (url: string) => {
	let result: string;
	result = url.replace(/https?:\/\//, '').toLowerCase();

	if (-1 !== result.search(/(.*)\/$/)) {
		result = result.slice(0, -1);
	}

	return result;
};
